import React, { useEffect, useState } from "react";
import {
  Row, Col, Card, DatePicker, Button,
  Table, notification, Spin, Statistic, Tag, Space
} from "antd";
import axios from "axios";
import { baseUrl } from "../../config";
import {
  AuditOutlined, UserOutlined,
  CheckCircleOutlined, CloseCircleOutlined,
  FileTextOutlined, ReloadOutlined
} from "@ant-design/icons";
import moment from 'moment';

const { RangePicker } = DatePicker;

function ApprovalWorkflowReport() {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([moment().subtract(6, 'months').startOf('month'), moment().endOf('month')]);
  const [workflowData, setWorkflowData] = useState([]);
  const [summaryStats, setSummaryStats] = useState({ totalActions: 0, approvedCount: 0, rejectedCount: 0 });

  const columns = [
    { title: 'Txn ID', dataIndex: 'txn_id', key: 'txn_id', width: 120,
      render: (v) => <span style={{ color: '#7a0000', fontWeight: 600, fontSize: 12 }}>{v}</span> },
    { title: 'Beneficiary', dataIndex: 'beneficiary_name', key: 'beneficiary_name',
      render: (name) => (
        <span style={{ fontSize: 12 }}>
          <UserOutlined style={{ color: '#7a0000', marginRight: 4 }} />{name}
        </span>
      ) },
    { title: 'Purpose', dataIndex: 'purpose_name', key: 'purpose_name',
      render: (v) => <span style={{ fontSize: 12 }}>{v}</span> },
    { title: 'Amount', dataIndex: 'amount', key: 'amount',
      render: (v) => <span style={{ color: '#52c41a', fontWeight: 600, fontSize: 12 }}>₹{Number(v).toLocaleString('en-IN')}</span> },
    { title: 'Action', dataIndex: 'action', key: 'action',
      render: (action) => {
        const colors = { Submitted: 'blue', Approved: 'success', Rejected: 'error', Returned: 'warning' };
        const icons = { Submitted: <FileTextOutlined />, Approved: <CheckCircleOutlined />, Rejected: <CloseCircleOutlined />, Returned: <CloseCircleOutlined /> };
        return <Tag color={colors[action]} icon={icons[action]}>{action}</Tag>;
      } },
    { title: 'Approver', dataIndex: 'approver_name', key: 'approver_name',
      render: (v) => <span style={{ fontSize: 12 }}>{v}</span> },
    { title: 'Date', dataIndex: 'action_date', key: 'action_date',
      render: (date) => (
        <div>
          <div style={{ fontSize: 12 }}>{moment(date).format('DD MMM YYYY')}</div>
          <div style={{ fontSize: 11, color: '#999' }}>{moment(date).format('HH:mm')}</div>
        </div>
      ) },
    { title: 'Comments', dataIndex: 'comments', key: 'comments',
      render: (v) => <span style={{ fontSize: 11, color: '#666' }}>{v ? (v.length > 40 ? `${v.substring(0, 40)}…` : v) : '—'}</span> },
  ];

  const approverColumns = [
    { title: 'Approver', dataIndex: 'name', key: 'name',
      render: (v) => <span style={{ fontWeight: 600, fontSize: 13 }}>{v}</span> },
    { title: 'Total', dataIndex: 'totalActions', key: 'totalActions', align: 'center',
      render: (v) => <span style={{ fontSize: 12 }}>{v}</span> },
    { title: 'Approved', dataIndex: 'approved', key: 'approved', align: 'center',
      render: (v) => <span style={{ color: '#52c41a', fontSize: 12 }}>{v}</span> },
    { title: 'Rejected', dataIndex: 'rejected', key: 'rejected', align: 'center',
      render: (v) => <span style={{ color: '#ff4d4f', fontSize: 12 }}>{v}</span> },
    { title: 'Rate', key: 'rate', align: 'center',
      render: (_, r) => <span style={{ fontSize: 12 }}>{r.totalActions > 0 ? ((r.approved / r.totalActions) * 100).toFixed(1) : 0}%</span> },
  ];

  useEffect(() => { fetchWorkflowReport(); }, [dateRange]);

  const fetchWorkflowReport = async () => {
    try {
      setLoading(true);
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');
      const response = await axios.get(`${baseUrl}/approval-workflow/list?start_date=${startDate}&end_date=${endDate}&limit=all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      if (response.data.status) {
        const data = response.data.result;
        setWorkflowData(data);
        setSummaryStats({
          totalActions: data.length,
          approvedCount: data.filter(d => d.action === 'Approved').length,
          rejectedCount: data.filter(d => d.action === 'Rejected').length,
        });
      }
    } catch {
      notification.error({ message: 'Error', description: 'Failed to fetch approval workflow report' });
    } finally {
      setLoading(false);
    }
  };

  const getApproverStats = () => {
    const map = {};
    workflowData.forEach(item => {
      if (!map[item.approver_name]) map[item.approver_name] = { name: item.approver_name, totalActions: 0, approved: 0, rejected: 0 };
      map[item.approver_name].totalActions++;
      if (item.action === 'Approved') map[item.approver_name].approved++;
      if (item.action === 'Rejected') map[item.approver_name].rejected++;
    });
    return Object.values(map).sort((a, b) => b.totalActions - a.totalActions);
  };

  const approverStats = getApproverStats();
  const approvalRate = summaryStats.totalActions > 0 ? ((summaryStats.approvedCount / summaryStats.totalActions) * 100).toFixed(1) : 0;

  const statCards = [
    { label: 'Total Actions',  value: summaryStats.totalActions,  accent: '#7a0000', bg: '#eef2f8', icon: <AuditOutlined /> },
    { label: 'Approved',       value: summaryStats.approvedCount, accent: '#15803d', bg: '#dcfce7', icon: <CheckCircleOutlined /> },
    { label: 'Rejected',       value: summaryStats.rejectedCount, accent: '#dc2626', bg: '#fee2e2', icon: <CloseCircleOutlined /> },
    { label: 'Approval Rate',  value: `${approvalRate}%`,         accent: '#d97706', bg: '#fef3c7', icon: <FileTextOutlined /> },
  ];

  return (
    <Spin spinning={loading}>
    <div style={{ padding: '20px 24px' }}>

      {/* Filter bar */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>Period:</span>
        <RangePicker value={dateRange} onChange={v => v && setDateRange(v)} format="DD-MM-YYYY" />
        <Button icon={<ReloadOutlined />} onClick={fetchWorkflowReport}
          style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff', borderRadius: 8, fontWeight: 600 }}>
          Generate
        </Button>
      </div>

      {/* Summary stat cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {statCards.map((sc, i) => (
          <Col key={i} xs={12} sm={6}>
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', borderTop: `3px solid ${sc.accent}`, padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{sc.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: sc.accent }}>{sc.value}</div>
                </div>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: sc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: sc.accent }}>
                  {sc.icon}
                </div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* Tables */}
      <Row gutter={[12, 12]}>
        <Col xs={24} lg={16}>
          <Card title={<span style={{ color: '#7a0000', fontWeight: 700, fontSize: 14 }}>Workflow Activity</span>}
            headStyle={{ borderBottom: '1.5px solid #e2e8f0' }} size="small">
            <Table columns={columns} size="small" scroll={{ x: 'max-content' }} pagination={{ pageSize: 10 }}
              dataSource={workflowData.map((item, i) => ({ key: i, ...item }))} />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title={<span style={{ color: '#7a0000', fontWeight: 700, fontSize: 14 }}>Approver Performance</span>}
            headStyle={{ borderBottom: '1.5px solid #e2e8f0' }} size="small">
            <Table columns={approverColumns} size="small" pagination={false}
              dataSource={approverStats.map((item, i) => ({ key: i, ...item }))} />
          </Card>
        </Col>
      </Row>
    </div>
    </Spin>
  );
}

export default ApprovalWorkflowReport;
